import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className={cn("flex gap-4", isMobile ? "flex-col items-start" : "items-center")}>
        {[1, 2, 3].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col space-y-6">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-medium text-foreground hover:text-primary/80 transition-colors font-sans" onClick={closeMobileMenu} data-index={i}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-2 py-2 text-sm font-medium transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50 font-sans relative", "after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className="fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ease-in-out border-b border-transparent bg-background ![padding:15px_0px_15px_0px] !h-[40px]">
      <div className="container mx-auto px-4 flex items-center justify-between !h-[20px] md:px-6">
        {}
        <Link to="/" className="flex-shrink-0 z-50 relative">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1554289/img/image_3n7kwtay.png" alt="Verifidie" className="h-10 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="17">
            <HeaderMenuConsumer />
          </MenuProvider>
          
          {}
          <Button asChild variant="default" size="sm" className="ml-4 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans tracking-wide">
            <Link to="tel:9718285422" className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs gap-1.5 has-[>svg]:px-2.5 ml-4 bg-primary text-primary-foreground rounded-full px-6 font-sans tracking-wide !h-[25px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">Consult Now</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="hover:bg-transparent focus:bg-transparent active:bg-transparent" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6 text-foreground" /> : <Menu className="h-6 w-6 text-foreground" />}
          </Button>
        </div>

        {}
        <div className={cn("fixed inset-0 bg-background z-40 flex flex-col justify-center px-8 transition-all duration-500 ease-in-out md:hidden", isMobileMenuOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-full pointer-events-none")}>
          <div className="flex flex-col space-y-8">
            <MenuProvider menu_id="17">
              <HeaderMenuConsumer isMobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="pt-8 border-t border-border/20">
              <Button asChild className="w-full text-lg py-6 rounded-full" onClick={() => setIsMobileMenuOpen(false)}>
                <Link to="tel:9718285422">Schedule Consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>;
}